import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  OnInit,
  Signal,
  signal,
  untracked,
  viewChildren,
  WritableSignal
} from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  CardComponent,
  CardGroupComponent,
  InputComponent,
  MbsPopupType,
  MbsSize,
  SelectComponent,
  TextEllipsisComponent
} from 'mbs-ui-kit';
import { unidentifiedErrorText } from 'mbs-ui-shared@interceptors/error-handler.interceptor';
import { first } from 'rxjs';
import {
  AzureInstanceOption,
  AzureInstanceStepValue,
  DefaultAzureInstanceStepValue,
  DisabledBootDiagnostic
} from 'wizards/models/azure-instance-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import {
  AzureBootDiagnosticStorageType,
  AzureConnectionType,
  AzureContainerType,
  AzureLocationType,
  AzureNetworkType,
  AzureStorageType,
  AzureSubnetType,
  AzureVMSizeType,
  RestoreToCloudService
} from 'wizards/services/restore-to-cloud.service';
import { ComputerNameMaxLength, ComputerNameValidationDirective, isComputerNameValid } from './computer-name-validation.directive';

type AzureInstanceControlKey = keyof Omit<AzureInstanceStepValue, 'valid'>;
type AzureInstanceSelectControlKey = Exclude<AzureInstanceControlKey, 'computerName'>;
type AzureNameValueType = { name?: string; value?: string };
type AzureConnectionOption = AzureInstanceOption & { region?: string };

const RestoredComputerNameSuffix = '-restored';

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertComponent,
    CardComponent,
    CardGroupComponent,
    ComputerNameValidationDirective,
    FormsModule,
    I18NextPipe,
    InputComponent,
    SelectComponent,
    TextEllipsisComponent
  ],
  selector: 'mbs-azure-instance-step',
  standalone: true,
  templateUrl: './azure-instance-step.component.html'
})
export class AzureInstanceStepComponent implements OnInit {
  mainService = inject(RemoteManagementWizardsService);
  restoreToCloudService = inject(RestoreToCloudService);
  private readonly initializedSelectKeys = new Set<AzureInstanceSelectControlKey>();
  private pendingLocationSyncAccountId: string | null = null;

  public readonly MbsSize = MbsSize;
  public readonly popupType = MbsPopupType;

  public readonly isTemporary = input<boolean>(false);
  public readonly prefixName = input<string>('');

  public readonly connectionsLoading: WritableSignal<boolean> = signal(false);
  public readonly locationsLoading: WritableSignal<boolean> = signal(false);
  public readonly resourceGroupsLoading: WritableSignal<boolean> = signal(false);
  public readonly vmSizesLoading: WritableSignal<boolean> = signal(false);
  public readonly networksLoading: WritableSignal<boolean> = signal(false);
  public readonly subnetsLoading: WritableSignal<boolean> = signal(false);
  public readonly storagesLoading: WritableSignal<boolean> = signal(false);
  public readonly containersLoading: WritableSignal<boolean> = signal(false);
  public readonly bootDiagnosticStoragesLoading: WritableSignal<boolean> = signal(false);

  public readonly error: WritableSignal<string> = signal('');

  public readonly azureAccounts: WritableSignal<AzureConnectionOption[]> = signal([]);
  public readonly locations: WritableSignal<AzureInstanceOption[]> = signal([]);
  public readonly resourceGroups: WritableSignal<string[]> = signal([]);
  public readonly vmSizes: WritableSignal<AzureInstanceOption[]> = signal([]);
  public readonly networks: WritableSignal<AzureInstanceOption[]> = signal([]);
  public readonly subnets: WritableSignal<AzureInstanceOption[]> = signal([]);
  public readonly storages: WritableSignal<AzureInstanceOption[]> = signal([]);
  public readonly containers: WritableSignal<AzureInstanceOption[]> = signal([]);
  public readonly bootDiagnosticStorages: WritableSignal<AzureInstanceOption[]> = signal([DisabledBootDiagnostic]);

  public readonly value = model<AzureInstanceStepValue>(DefaultAzureInstanceStepValue);
  public readonly ngModels = viewChildren(NgModel);

  public readonly azureAccount: WritableSignal<string> = signal(DefaultAzureInstanceStepValue.azureAccount);
  public readonly computerName: WritableSignal<string> = signal(DefaultAzureInstanceStepValue.computerName);
  public readonly location: WritableSignal<string> = signal(DefaultAzureInstanceStepValue.location);
  public readonly resourceGroup: WritableSignal<string> = signal(DefaultAzureInstanceStepValue.resourceGroup);
  public readonly vmSize: WritableSignal<string> = signal(DefaultAzureInstanceStepValue.vmSize);
  public readonly network: WritableSignal<string> = signal(DefaultAzureInstanceStepValue.network);
  public readonly subnet: WritableSignal<string> = signal(DefaultAzureInstanceStepValue.subnet);
  public readonly storage: WritableSignal<string> = signal(DefaultAzureInstanceStepValue.storage);
  public readonly container: WritableSignal<string> = signal(DefaultAzureInstanceStepValue.container);
  public readonly bootDiagnosticStorage: WritableSignal<string> = signal(DefaultAzureInstanceStepValue.bootDiagnosticStorage);

  public readonly idPrefix = computed(() => (this.isTemporary() ? 'azure-temporary-instance' : 'azure-target-instance'));
  public readonly loading: Signal<boolean> = computed(
    () =>
      this.connectionsLoading() ||
      this.locationsLoading() ||
      this.resourceGroupsLoading() ||
      this.vmSizesLoading() ||
      this.networksLoading() ||
      this.subnetsLoading() ||
      this.storagesLoading() ||
      this.containersLoading() ||
      this.bootDiagnosticStoragesLoading()
  );

  private accountsMap: { [key: string]: string } = {};
  private regionsMap: { [key: string]: string } = {};
  private subnetsMap: { [key: string]: string } = {};

  constructor() {
    this.initBaseDataAndSubscribes();
  }

  /**
   * Requests are started here and not in the constructor:
   * cached responses are emitted synchronously, and a write to the `value` model
   * made before Angular applies the initial binding would be lost
   * */
  public ngOnInit(): void {
    this.getConnections();
  }

  public isSelectDisabled(items: ReadonlyArray<string | AzureInstanceOption>): boolean {
    return !items.length || this.loading();
  }

  public valueChangeHandler(key: AzureInstanceControlKey, value: string | null | undefined): void {
    const currentValue = this[key]();
    const nextValue = value ?? '';

    if (key === 'azureAccount' && currentValue !== nextValue) this.pendingLocationSyncAccountId = nextValue || null;
    if (key === 'location') this.pendingLocationSyncAccountId = null;

    if (key !== 'computerName') this.initializedSelectKeys.add(key);
    this[key].set(nextValue);

    this.value.update((currentValue) => {
      const newValue = {
        ...(currentValue || DefaultAzureInstanceStepValue),
        ...this.getDependentValues(key, nextValue),
        [key]: nextValue
      };

      return { ...newValue, valid: this.isStepValid(newValue) };
    });
  }

  public forceValid(): void {
    this.ngModels().forEach((ngModel) => this.forceValidState(ngModel));
    this.updateValidState();
  }

  // Private section
  private initBaseDataAndSubscribes(): void {
    effect(() => {
      const azureAccount = this.azureAccount();

      untracked(() => {
        this.getLocations(azureAccount);
        this.getResourceGroups(azureAccount);
      });
    });

    effect(() => {
      const azureAccount = this.azureAccount();
      const location = this.location();

      untracked(() => this.getVMSizes(azureAccount, location));
    });

    effect(() => {
      const azureAccount = this.azureAccount();
      const location = this.location();
      const resourceGroup = this.resourceGroup();
      const storage = this.storage();

      untracked(() => {
        this.getNetworks(azureAccount, location, resourceGroup);
        this.getStorages(azureAccount, location, resourceGroup);
        this.getContainers(azureAccount, storage, resourceGroup);
        this.getBootDiagnosticStorages(azureAccount, location, resourceGroup);
      });
    });

    effect(() => {
      const azureAccount = this.azureAccount();
      const location = this.location();
      const resourceGroup = this.resourceGroup();
      const network = this.network();

      untracked(() => this.getSubnets(azureAccount, location, resourceGroup, network));
    });

    effect(() => {
      const prefixName = this.prefixName();

      if (!prefixName) return;

      queueMicrotask(() => this.updateComputerName(prefixName));
    });
    effect(() => {
      this.loading();
      this.isTemporary();

      untracked(() => this.updateValidState());
    });
  }

  private getConnections(): void {
    this.startLoading(this.connectionsLoading);

    this.restoreToCloudService
      .getAzureConnections(this.mainService.targetComputerData?.userId || this.mainService.userId)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: AzureConnectionType[]) => {
          const accounts: AzureConnectionOption[] =
            result?.map((el) => ({ ...el, id: el.id, name: el.name || el.bucket || el.id || '' })) || [];

          accounts.forEach((account: AzureConnectionOption) => (this.accountsMap[account.id] = account.connectionId));
          this.azureAccounts.set(accounts);
          this.setFirstOptionValueIfEmpty('azureAccount', accounts);
          this.connectionsLoading.set(false);
        },
        error: (error: unknown) => {
          this.setLoadError(error);
          this.azureAccounts.set([]);
          this.setFirstOptionValueIfEmpty('azureAccount', []);
          this.connectionsLoading.set(false);
        }
      });
  }

  private getLocations(connectionId: string): void {
    if (!connectionId) return;

    this.startLoading(this.locationsLoading);

    this.restoreToCloudService
      .getAzureLocations(connectionId)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: AzureLocationType[]) => {
          const locations = this.mapNameValueOptions(result);

          locations.forEach((location) => (this.regionsMap[location.id] = location.region));
          this.locations.set(locations);
          this.setLocation(locations, connectionId);
          this.locationsLoading.set(false);
        },
        error: (error: unknown) => {
          this.setLoadError(error);
          this.locations.set([]);
          this.setLocation([], connectionId);
          this.locationsLoading.set(false);
        }
      });
  }

  private getResourceGroups(connectionId: string): void {
    if (!connectionId) return;

    this.startLoading(this.resourceGroupsLoading);

    this.restoreToCloudService
      .getAzureResourceGroups(connectionId)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: string[]) => {
          const resourceGroups = result || [];

          this.resourceGroups.set(resourceGroups);
          this.syncValueWithAvailableValues('resourceGroup', resourceGroups[0], resourceGroups);
          this.resourceGroupsLoading.set(false);
        },
        error: (error: unknown) => {
          this.setLoadError(error);
          this.resourceGroups.set([]);
          this.syncValueWithAvailableValues('resourceGroup', null, []);
          this.resourceGroupsLoading.set(false);
        }
      });
  }

  private getVMSizes(connectionId: string, location: string): void {
    if (!connectionId || !location) return;

    this.startLoading(this.vmSizesLoading);

    this.restoreToCloudService
      .getAzureVMSizes(connectionId, location)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: AzureVMSizeType[]) => {
          const vmSizes = this.sortOptions(
            result?.map((el) => ({ ...el, id: el.name, name: `${el.name || ''} (Core(s) ${el.coresNum}) [${el.memoryInMb}]` })) || [],
            'id'
          );

          this.vmSizes.set(vmSizes);
          this.setFirstOptionValueIfEmpty('vmSize', vmSizes);
          this.vmSizesLoading.set(false);
        },
        error: (error: unknown) => {
          this.setLoadError(error);
          this.vmSizes.set([]);
          this.setFirstOptionValueIfEmpty('vmSize', []);
          this.vmSizesLoading.set(false);
        }
      });
  }

  private getNetworks(connectionId: string, location: string, resourceGroup: string): void {
    if (!connectionId || !location || !resourceGroup) return;

    this.startLoading(this.networksLoading);

    this.restoreToCloudService
      .getAzureNetworks(connectionId, location, resourceGroup)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: AzureNetworkType[]) => {
          const networks = this.sortOptions(result?.map((el) => ({ ...el, id: el.name, name: `${el.name} (${el.value})` })) || []);

          this.networks.set(networks);
          this.setFirstOptionValueIfEmpty('network', networks);
          this.networksLoading.set(false);
        },
        error: (error: unknown) => {
          this.setLoadError(error);
          this.networks.set([]);
          this.setFirstOptionValueIfEmpty('network', []);
          this.networksLoading.set(false);
        }
      });
  }

  private getSubnets(connectionId: string, location: string, resourceGroup: string, network: string): void {
    if (!connectionId || !location || !resourceGroup || !network) return;

    this.startLoading(this.subnetsLoading);

    this.restoreToCloudService
      .getAzureSubnets(connectionId, location, resourceGroup, network)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: AzureSubnetType[]) => {
          const subnets = this.sortOptions(this.mapExtendedNameValueOptions(result));

          result.forEach((subnet) => (this.subnetsMap[subnet.value] = subnet.name));
          this.subnets.set(subnets);
          this.setFirstOptionValueIfEmpty('subnet', subnets);
          this.subnetsLoading.set(false);
        },
        error: (error: unknown) => {
          this.setLoadError(error);
          this.subnets.set([]);
          this.setFirstOptionValueIfEmpty('subnet', []);
          this.subnetsLoading.set(false);
        }
      });
  }

  private getStorages(connectionId: string, location: string, resourceGroup: string): void {
    if (!connectionId || !location || !resourceGroup) return;

    this.startLoading(this.storagesLoading);

    this.restoreToCloudService
      .getAzureStorages(connectionId, location, resourceGroup)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: AzureStorageType[]) => {
          const storages = this.mapExtendedNameValueOptions(result);

          this.storages.set(storages);
          this.setFirstOptionValueIfEmpty('storage', storages);
          this.storagesLoading.set(false);
        },
        error: (error: unknown) => {
          this.setLoadError(error);
          this.storages.set([]);
          this.setFirstOptionValueIfEmpty('storage', []);
          this.storagesLoading.set(false);
        }
      });
  }

  private getContainers(connectionId: string, storage: string, resourceGroup: string): void {
    if (!connectionId || !storage || !resourceGroup) return;

    this.startLoading(this.containersLoading);

    this.restoreToCloudService
      .getAzureContainers(connectionId, storage, resourceGroup)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: AzureContainerType[]) => {
          const containers = this.mapNameValueOptions(result);

          this.containers.set(containers);
          this.setFirstOptionValueIfEmpty('container', containers);
          this.containersLoading.set(false);
        },
        error: (error: unknown) => {
          this.setLoadError(error);
          this.containers.set([]);
          this.setFirstOptionValueIfEmpty('container', []);
          this.containersLoading.set(false);
        }
      });
  }

  private getBootDiagnosticStorages(connectionId: string, location: string, resourceGroup: string): void {
    if (!connectionId || !location || !resourceGroup) return;

    this.startLoading(this.bootDiagnosticStoragesLoading);

    this.restoreToCloudService
      .getAzureBootDiagnosticStorages(connectionId, location, resourceGroup)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: AzureBootDiagnosticStorageType[]) => {
          const bootDiagnosticStorages = result?.map((el) => ({ ...el, id: el.name, name: `${el.value} (${el.name})` })) || [];
          const withDefault = [DisabledBootDiagnostic, ...bootDiagnosticStorages];

          this.bootDiagnosticStorages.set(withDefault);
          this.setFirstOptionValueIfEmpty('bootDiagnosticStorage', withDefault);
          this.bootDiagnosticStoragesLoading.set(false);
        },
        error: (error: unknown) => {
          this.setLoadError(error);
          this.bootDiagnosticStorages.set([]);
          this.setFirstOptionValueIfEmpty('bootDiagnosticStorage', []);
          this.bootDiagnosticStoragesLoading.set(false);
        }
      });
  }

  private startLoading(loading: WritableSignal<boolean>): void {
    this.error.set('');
    loading.set(true);
  }

  private setLoadError(error: unknown): void {
    const httpError = error as HttpErrorResponse;

    if (httpError?.status !== HttpStatusCode.BadRequest) return;

    const body = httpError.error;
    const message = typeof body === 'string' ? body : body?.detail || body?.title || body?.message;

    this.error.set(message || unidentifiedErrorText);
  }

  private sortOptions<T extends AzureInstanceOption>(items: T[], key = 'name'): T[] {
    return [...items].sort(
      (first, second) => this.compareValues(first?.[key], second?.[key]) || this.compareValues(first?.name, second?.name)
    );
  }

  private compareValues(first: unknown, second: unknown): number {
    return String(first ?? '').localeCompare(String(second ?? ''), undefined, { sensitivity: 'base' });
  }

  private mapExtendedNameValueOptions(items: AzureNameValueType[]): AzureInstanceOption[] {
    return items?.map((el) => ({ ...el, id: el.value, name: `${el.name} (${el.value})` })) || [];
  }

  private mapNameValueOptions(items: AzureNameValueType[]): AzureInstanceOption[] {
    return items?.map((el) => ({ ...el, id: el.value, name: el.name || el.value || '' })) || [];
  }

  private setFirstOptionValueIfEmpty(key: AzureInstanceSelectControlKey, items: ReadonlyArray<AzureInstanceOption>, force = false): void {
    this.syncValueWithAvailableValues(
      key,
      items[0]?.id,
      items.map((item) => item.id),
      force
    );
  }

  private setLocation(locations: AzureInstanceOption[], accountId: string): void {
    if (accountId !== this.azureAccount()) return;

    const selectedAccount = this.azureAccount();
    const current = this.location();
    const forceLocationSync = this.pendingLocationSyncAccountId === accountId;
    const foundAccount = this.azureAccounts()?.find((account) => account.id === selectedAccount);

    if (forceLocationSync) this.pendingLocationSyncAccountId = null;

    if (!foundAccount) return this.setFirstOptionValueIfEmpty('location', locations, forceLocationSync);

    const foundLocation = locations?.find((location) => location.id === foundAccount.region);

    if (!foundLocation) return this.setFirstOptionValueIfEmpty('location', locations, forceLocationSync);

    if (foundLocation.id === current) return;

    this.syncValueWithAvailableValues(
      'location',
      foundLocation.id,
      locations.map((location) => location.id),
      forceLocationSync
    );
  }

  private syncValueWithAvailableValues(
    key: AzureInstanceSelectControlKey,
    value?: string,
    availableValues: ReadonlyArray<string | undefined> = value ? [value] : [],
    force = false
  ): void {
    const currentValue = this[key]();
    const isInitialized = this.initializedSelectKeys.has(key);

    this.initializedSelectKeys.add(key);

    if (force && value) {
      if (currentValue !== value) {
        this.valueChangeHandler(key, value);
        untracked(() => this.updateValidState());
      }

      return;
    }

    if (currentValue && !availableValues.includes(currentValue)) {
      this.valueChangeHandler(key, '');
      untracked(() => this.updateValidState());
      return;
    }

    if (currentValue || !value || isInitialized) return;

    this.valueChangeHandler(key, value);
    untracked(() => this.updateValidState());
  }

  private updateComputerName(name: string): void {
    const computerName = untracked(() => this.computerName());

    if (computerName) return;

    const prefix = name.slice(0, ComputerNameMaxLength - RestoredComputerNameSuffix.length);

    this.valueChangeHandler('computerName', prefix + RestoredComputerNameSuffix);
  }

  private forceValidState(ngModel: NgModel): void {
    if (!ngModel) return;

    ngModel.control.markAsDirty();
    ngModel.control.markAsTouched();
    ngModel.control.updateValueAndValidity();
  }

  private updateValidState(): void {
    this.value.update((currentValue) => {
      const value = this.withSelectedValues(currentValue || DefaultAzureInstanceStepValue);

      return { ...value, valid: this.isStepValid(value) };
    });
  }

  private withSelectedValues(value: AzureInstanceStepValue): AzureInstanceStepValue {
    return this.getRequiredKeys().reduce((acc, key) => {
      const selected = this[key]();

      if (!selected || acc[key] === selected) return acc;

      return { ...acc, ...this.getDependentValues(key, selected), [key]: selected };
    }, value);
  }

  private getDependentValues(key: AzureInstanceControlKey, value: string): Partial<AzureInstanceStepValue> {
    if (key === 'azureAccount') return { connectionId: this.accountsMap[value] || '' };
    if (key === 'location') return { region: this.regionsMap[value] || '' };
    if (key === 'subnet') return { subnetName: this.subnetsMap[value] || '' };

    return {};
  }

  private isStepValid(value: AzureInstanceStepValue): boolean {
    const isComputerNameCorrect = this.isTemporary() || isComputerNameValid(value?.computerName);

    return !this.loading() && isComputerNameCorrect && this.getRequiredKeys().every((key) => !!value?.[key]);
  }

  private getRequiredKeys(): AzureInstanceControlKey[] {
    const keys: AzureInstanceControlKey[] = [
      'azureAccount',
      'location',
      'resourceGroup',
      'vmSize',
      'network',
      'subnet',
      'storage',
      'container',
      'bootDiagnosticStorage'
    ];

    return this.isTemporary() ? keys : ['computerName', ...keys];
  }
}
